import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader, ArrowRight, Home, Crown } from 'lucide-react';

// ============================================
// SUBSCRIPTION SUCCESS PAGE
// ============================================
export const SubscriptionSuccess = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

  useEffect(() => {
    // Give Stripe webhook time to process
    const timer = setTimeout(() => {
      if (user?.id) {
        fetchSubscriptionStatus();
      } else {
        setLoading(false);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/stripe/subscription-status/${user.id}`);
      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  // Navigation helper - uses tab-based navigation
  const navigateTo = (tab) => {
    window.location.href = `/?tab=${tab}`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        gap: '1rem'
      }}>
        <Loader size={48} style={{ color: '#10b981', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#94a3b8' }}>Setting up your subscription...</p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <div style={{
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '1rem',
        padding: '3rem 2rem'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(16, 185, 129, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <CheckCircle size={48} color="#10b981" />
        </div>

        <h1 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#10b981',
          marginBottom: '0.75rem'
        }}>
          Welcome to InvestorQ {subscription?.tier ? subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1) : 'Pro'}!
        </h1>

        <p style={{
          fontSize: '1.1rem',
          color: '#94a3b8',
          marginBottom: '1.5rem'
        }}>
          Your subscription is now active. You have full access to all features.
        </p>

        {subscription?.isTrialing && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ color: '#f59e0b', margin: 0, fontSize: '0.95rem' }}>
              🎉 Your 7-day free trial has started!
              <br />
              <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                Trial ends on {subscription.trialEnd ? new Date(subscription.trialEnd).toLocaleDateString() : 'in 7 days'}
              </span>
            </p>
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => navigateTo('home')}
            style={{
              padding: '0.875rem 1.5rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Home size={18} />
            Go to Dashboard
          </button>
          <button
            onClick={() => navigateTo('settings')}
            style={{
              padding: '0.875rem 1.5rem',
              background: 'transparent',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              color: '#94a3b8',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Manage Subscription
          </button>
        </div>
      </div>

      {/* Quick Tips */}
      <div style={{
        marginTop: '2rem',
        textAlign: 'left',
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          🚀 Get Started with Your New Features
        </h3>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <QuickTip text="Analyze any ETF with X-Ray" tab="etf-xray" />
          <QuickTip text="Compare ETFs side-by-side" tab="etf-compare" />
          <QuickTip text="Explore Options Strategies" tab="options" />
          <QuickTip text="Track Congress Trades" tab="congress-trades" />
        </ul>
      </div>
    </div>
  );
};

const QuickTip = ({ text, tab }) => (
  <li
    onClick={() => window.location.href = `/?tab=${tab}`}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#60a5fa',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      transition: 'background 0.2s'
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
  >
    <ArrowRight size={16} />
    {text}
  </li>
);

// ============================================
// SUBSCRIPTION CANCEL PAGE
// ============================================
export const SubscriptionCancel = () => {
  // Navigation helper - uses tab-based navigation
  const navigateTo = (tab) => {
    window.location.href = `/?tab=${tab}`;
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '1rem',
        padding: '3rem 2rem'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(245, 158, 11, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <XCircle size={48} color="#f59e0b" />
        </div>

        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: 'bold',
          color: '#f59e0b',
          marginBottom: '0.75rem'
        }}>
          Checkout Canceled
        </h1>

        <p style={{
          fontSize: '1.1rem',
          color: '#94a3b8',
          marginBottom: '1.5rem'
        }}>
          No worries! You weren't charged. Your subscription wasn't created.
        </p>

        <p style={{
          fontSize: '0.95rem',
          color: '#64748b',
          marginBottom: '2rem'
        }}>
          Changed your mind? You can always come back and start your free trial later.
        </p>

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => navigateTo('subscription')}
            style={{
              padding: '0.875rem 1.5rem',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Crown size={18} />
            View Plans Again
          </button>
          <button
            onClick={() => navigateTo('home')}
            style={{
              padding: '0.875rem 1.5rem',
              background: 'transparent',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              color: '#94a3b8',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Home size={18} />
            Go to Dashboard
          </button>
        </div>
      </div>

      {/* Feedback Section */}
      <div style={{
        marginTop: '2rem',
        background: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <p style={{ color: '#94a3b8', marginBottom: '0.5rem' }}>
          Have questions or concerns?
        </p>
        <a
          href="mailto:info@sportee.us"
          style={{
            color: '#60a5fa',
            textDecoration: 'none',
            fontWeight: '600'
          }}
        >
          Contact our support team →
        </a>
      </div>
    </div>
  );
};

export default { SubscriptionSuccess, SubscriptionCancel };